import React, { useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { Check, ChevronRight, GripVertical, MousePointer2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { usePresentationMode } from "./PresentationContext";
import HealthPill from "./HealthPill";
import { SelectedCenterSummary } from "./SelectedCenterSummary";
import { ProductSwapDialog } from "./ProductSwapDialog";
import { DEFAULT_SLOT_MAP, SLOT_DEFINITIONS, SLOT_STORAGE_KEY, readSlotMap } from "../../lib/showcaseSlots";

const ENTRANCE = { duration: 0.22, ease: "easeOut" };
const productHasNew = (product) =>
  [...(product.done || []), ...(product.next || []), ...(product.blocked || [])].some((item) => item?.is_new);

const NodeCard = ({ product, active, onSelect, mobile }) => (
  <Button type="button" variant="outline" onClick={() => onSelect(product.slug)} aria-pressed={active}
    aria-controls={mobile ? `wheel-mobile-preview-${product.slug}` : "wheel-center-preview"}
    data-testid={`wheel-node-${mobile ? "mobile-" : ""}${product.slug}`} className="wheel-product-card hover:bg-card hover:text-card-foreground"
    style={{ "--product-swatch": product.swatch }}>
    <span className="flex min-w-0 flex-1 flex-col items-start gap-2">
      <span className="flex w-full items-start gap-2">
        <span className="wheel-product-dot mt-1.5" aria-hidden="true" />
        <span className="min-w-0 flex-1 font-heading text-base font-semibold leading-tight" data-testid={`wheel-name-${mobile ? "mobile-" : ""}${product.slug}`}>{product.display_name}</span>
        {active && <Check className="wheel-selection-check" aria-hidden="true" />}
        {mobile && <ChevronRight aria-hidden="true" />}
      </span>
      <span className="pr-9"><HealthPill health={product.health} testId={`wheel-health-${mobile ? "mobile-" : ""}${product.slug}`} /></span>
      {productHasNew(product) && <span className="new-marker rounded-full px-2 py-0.5 text-xs" data-testid={`wheel-new-${mobile ? "mobile-" : ""}${product.slug}`}>New updates</span>}
    </span>
  </Button>
);

const ProductSlot = ({ slot, product, selected, onSelect, onReorder, mobile, index }) => {
  const reducedMotion = useReducedMotion();
  const presenting = usePresentationMode();
  const { setNodeRef: dropRef, isOver } = useDroppable({ id: slot.id, disabled: presenting, data: { product: product?.slug } });
  const { attributes, listeners, setNodeRef: dragRef, setActivatorNodeRef, isDragging } = useDraggable({ id: product?.slug || slot.id, disabled: presenting || !product });
  if (!product) return <div ref={dropRef} data-testid={`showcase-slot-${mobile ? "mobile-" : ""}${slot.id}`} className="wheel-product-slot" />;
  return (
    <motion.div ref={dropRef} className={`wheel-product-slot ${isOver && !isDragging ? "wheel-slot-over" : ""}`}
      data-testid={`showcase-slot-${mobile ? "mobile-" : ""}${slot.id}`} data-product-slug={product.slug} aria-label={`${slot.label}: ${product.display_name}`}
      initial={reducedMotion ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={reducedMotion ? { duration: 0 } : { ...ENTRANCE, delay: index * 0.025 }}>
      <div ref={dragRef} className={`wheel-reorderable-card ${isDragging ? "opacity-50" : ""}`}>
        <NodeCard product={product} active={selected === product.slug} onSelect={onSelect} mobile={mobile} />
        {!presenting && <Button ref={setActivatorNodeRef} type="button" variant="ghost" size="icon" className="wheel-drag-handle" {...attributes} {...listeners}
          aria-label={`Reorder ${product.display_name}`} aria-describedby="showcase-reorder-instructions"
          title="Drag onto another card to swap, or click to choose a card"
          data-testid={`showcase-card-drag-handle-${mobile ? "mobile-" : ""}${product.slug}`}
          onClick={() => onReorder(product.slug, mobile)}>
          <GripVertical aria-hidden="true" />
        </Button>}
      </div>
    </motion.div>
  );
};

const SelectorBoard = ({ products, slots, selected, onSelect, onOpenDetails, onSwap, onReorder, mobile = false }) => {
  const [dragging, setDragging] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const selection = products.find((p) => p.slug === selected);
  const draggedProduct = products.find((p) => p.slug === dragging);
  const renderSlot = (slot) => <ProductSlot key={slot.id} slot={slot} product={products.find((p) => p.slug === slots[slot.id])} selected={selected} onSelect={onSelect} onReorder={onReorder} mobile={mobile} index={SLOT_DEFINITIONS.indexOf(slot)} />;
  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={({ active }) => setDragging(active.id)}
      onDragCancel={() => setDragging(null)} onDragEnd={({ active, over }) => {
        if (over?.data.current?.product) onSwap(active.id, over.data.current.product);
        setDragging(null);
      }}>
      {mobile ? (
        <div className="space-y-3" data-testid="wheel-mobile-list">
          {SLOT_DEFINITIONS.map((slot) => (
            <div key={slot.id}>
              {renderSlot(slot)}
              <div id={`wheel-mobile-preview-${slots[slot.id]}`} aria-live="polite">
                {selection && selection.slug === slots[slot.id] && <div className="mt-2"><SelectedCenterSummary product={selection} onOpenDetails={onOpenDetails} mobile /></div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="wheel-stage wheel-stage--rectangle" data-testid="wheel-stage">
          <div className="wheel-bank wheel-bank-top">{SLOT_DEFINITIONS.slice(0, 3).map(renderSlot)}</div>
          <div className="wheel-side wheel-side-left">{renderSlot(SLOT_DEFINITIONS[3])}</div>
          <div className="wheel-center" id="wheel-center-preview" aria-live="polite" aria-atomic="true">
            {selection ? <SelectedCenterSummary product={selection} onOpenDetails={onOpenDetails} /> : (
              <Card className="wheel-center-summary-card wheel-center-prompt" data-testid="wheel-center-empty">
                <MousePointer2 className="text-muted-foreground" aria-hidden="true" />
                <h3 className="font-heading text-2xl font-semibold" data-testid="wheel-center-prompt-title">Choose a product</h3>
                <p className="max-w-xs text-sm leading-relaxed text-muted-foreground" data-testid="wheel-center-prompt-description">See what’s done and what’s next here, with the full detail below.</p>
              </Card>
            )}
          </div>
          <div className="wheel-side wheel-side-right">{renderSlot(SLOT_DEFINITIONS[4])}</div>
          <div className="wheel-bank wheel-bank-bottom">{SLOT_DEFINITIONS.slice(5).map(renderSlot)}</div>
        </div>
      )}
      <DragOverlay dropAnimation={null}>
        {draggedProduct && <Card className="wheel-drag-preview product-identity-surface p-4" style={{ "--product-swatch": draggedProduct.swatch }} data-testid={`showcase-drag-preview-${mobile ? "mobile" : "desktop"}`}><p className="font-heading text-lg font-semibold">{draggedProduct.display_name}</p><p className="mt-1 text-xs text-muted-foreground">Drop on a card to swap</p></Card>}
      </DragOverlay>
    </DndContext>
  );
};

export const WheelChart = ({ products = [], selected, onSelect, onOpenDetails }) => {
  const presenting = usePresentationMode();
  const [layout, setLayout] = useState(readSlotMap);
  const [reorder, setReorder] = useState(null);
  const lastHandle = useRef(null);
  const persist = (slots, message) => {
    try {
      localStorage.setItem(SLOT_STORAGE_KEY, JSON.stringify({ schemaVersion: 1, slots }));
      setLayout({ slots, message: `${message} Saved on this browser.`, error: false });
    } catch (error) {
      setLayout({ slots, message: `${message} Could not save; this layout will last only for this visit.`, error: true });
    }
  };
  const swap = (source, target) => {
    if (presenting || !source || source === target) return;
    const from = SLOT_DEFINITIONS.find((s) => layout.slots[s.id] === source);
    const to = SLOT_DEFINITIONS.find((s) => layout.slots[s.id] === target);
    if (!from || !to) return;
    const sourceName = products.find((p) => p.slug === source)?.display_name || source;
    const targetName = products.find((p) => p.slug === target)?.display_name || target;
    persist({ ...layout.slots, [from.id]: target, [to.id]: source }, `${sourceName} moved to ${to.label.toLowerCase()}, swapping with ${targetName}.`);
  };
  const openReorder = (slug, mobile) => {
    lastHandle.current = `showcase-card-drag-handle-${mobile ? "mobile-" : ""}${slug}`;
    setReorder(slug);
  };
  const returnFocus = () => requestAnimationFrame(() => {
    if (lastHandle.current) document.querySelector(`[data-testid="${lastHandle.current}"]`)?.focus();
  });
  const boardProps = { products, slots: layout.slots, selected, onSelect, onOpenDetails, onSwap: swap, onReorder: openReorder };
  if (!products.length) return <Card className="p-6" data-testid="wheel-empty">No product updates are available yet. Use Refresh above to check again.</Card>;
  return (
    <div data-testid="wheel-chart">
      {!presenting && <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground" id="showcase-reorder-instructions" data-testid="showcase-reorder-instructions">Drag a grip onto another card to swap. Or click the grip to choose a card.</p>
        <Button variant="ghost" className="min-h-11 self-start text-xs" onClick={() => persist(DEFAULT_SLOT_MAP, "Starting layout restored.")} data-testid="showcase-reset-layout"><RotateCcw aria-hidden="true" />Reset layout</Button>
      </div>}
      <div className="hidden lg:block"><SelectorBoard {...boardProps} /></div>
      <div className="lg:hidden"><SelectorBoard {...boardProps} mobile /></div>
      {!presenting && <p className={`mt-3 text-xs leading-relaxed ${layout.error ? "text-destructive" : "text-muted-foreground"}`} role="status" aria-live="polite" data-testid="showcase-layout-status">{layout.message}</p>}
      {!presenting && <ProductSwapDialog source={reorder} products={products} slots={layout.slots} onClose={() => setReorder(null)} onSwap={swap} onReturnFocus={returnFocus} />}
    </div>
  );
};
